import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CheckCircle2, Gift, Link2, Receipt, Settings2, ShieldCheck, Store, Users } from "lucide-react";
import { SEO } from "@/components/SEO";
import { StructuredData } from "@/components/StructuredData";
import { SiteLayout } from "@/components/site/SiteLayout";
import { CtaBand } from "@/components/site/CtaBand";
import { CustomerJourneySection } from "@/components/site/CustomerJourneySection";
import { HowItWorksVisual } from "@/components/site/HowItWorksVisual";
import { howToSchema } from "@/lib/seoSchema";

const merchantFlow = [
  {
    icon: Store,
    title: "Install and brand it",
    body: "Turn on Splitkart, set the button text and colors, and keep the experience aligned with your storefront.",
  },
  {
    icon: Settings2,
    title: "Let Splitkart coordinate the split",
    body: "Organizer payments, invite links, retries, and payment tracking happen automatically without extra manual work for the merchant.",
  },
  {
    icon: Receipt,
    title: "Keep order handling clean",
    body: "Paid shares are tracked separately while the final customer-facing order is created only when the group completes payment.",
  },
];

const trustPoints = [
  "Organizer and all invited friends appear in live payment progress tracking.",
  "Interrupted payments do not burn the contribution link before expiry.",
  "Merchants continue using native Shopify or WooCommerce checkout flows.",
  "Customers get one clear journey instead of managing money manually in chat groups.",
];

const faqs = [
  {
    q: "Does the organizer have to pay first?",
    a: "Yes. The organizer completes their own share first, which activates the split and sends contribution links to friends.",
  },
  {
    q: "Do invited friends need to create a Splitkart account?",
    a: "No. Friends can pay their share through the secure contribution link without going through a heavy signup flow.",
  },
  {
    q: "What happens if someone closes the page during payment?",
    a: "The contribution link stays retryable until the split expiry window ends, so participants can come back and finish payment later.",
  },
  {
    q: "When does the merchant ship the order?",
    a: "The merchant fulfills only after the group reaches full payment and the final shippable order is created.",
  },
  {
    q: "Can the merchant keep the experience branded?",
    a: "Yes. The split button, cards, and key storefront touchpoints can be branded so the shopper still feels like they are buying from the merchant, not leaving for a separate platform.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.a,
    },
  })),
};

function HowSplitPaymentsWork() {
  return (
    <SiteLayout>
      <SEO
        title="How Split Payments Work"
        description="Learn how Splitkart helps shoppers split a purchase with friends while merchants keep native checkout, branded invites, and clean final order handling."
        url="/how-split-payments-work"
      />
      <StructuredData data={[howToSchema(), faqSchema]} />

      <section className="section bg-gradient-hero">
        <div className="container-tight">
          <div className="mx-auto max-w-3xl text-center">
            <span className="eyebrow">How split payments work</span>
            <h1 className="mt-5 text-balance text-4xl font-bold md:text-6xl">
              Let one shopper start the order.
              <span className="text-primary"> Let the whole group finish it together.</span>
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-lg text-muted-foreground">
              Splitkart turns one purchase into a guided group checkout flow. The organizer starts the order, friends receive secure links, and the merchant gets one clean final order when everyone has paid.
            </p>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-muted-foreground">
              It is designed for Shopify and WooCommerce brands that want a native-feeling split-payment experience, clearer invite flows, and higher-value group purchases without adding debt or checkout confusion.
            </p>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-3">
            <div className="rounded-3xl border border-border bg-card/90 p-6 shadow-soft">
              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-primary/10 p-3 text-primary">
                  <Gift className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold">For shoppers</p>
                  <p className="text-sm text-muted-foreground">Split expensive purchases without leaving the merchant flow.</p>
                </div>
              </div>
            </div>
            <div className="rounded-3xl border border-border bg-card/90 p-6 shadow-soft">
              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-primary/10 p-3 text-primary">
                  <Link2 className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold">For invited friends</p>
                  <p className="text-sm text-muted-foreground">Receive a dedicated link and pay only your portion.</p>
                </div>
              </div>
            </div>
            <div className="rounded-3xl border border-border bg-card/90 p-6 shadow-soft">
              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-primary/10 p-3 text-primary">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold">For merchants</p>
                  <p className="text-sm text-muted-foreground">Track every share and fulfill only after the order is fully funded.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <HowItWorksVisual />

      <CustomerJourneySection />

      <section className="section bg-surface">
        <div className="container-tight">
          <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-start">
            <div>
              <span className="eyebrow">Merchant side</span>
              <h2 className="mt-4 text-3xl font-bold md:text-5xl">A guided group purchase flow without messy manual coordination.</h2>
              <p className="mt-4 text-lg text-muted-foreground">
                Splitkart is designed to keep the merchant in control while removing the friction customers usually face when coordinating a shared purchase through group chats.
              </p>

              <div className="mt-8 space-y-4">
                {merchantFlow.map((item) => {
                  const Icon = item.icon;

                  return (
                    <div key={item.title} className="rounded-2xl border border-border bg-card p-6 shadow-soft">
                      <div className="flex items-start gap-4">
                        <div className="rounded-2xl bg-primary/10 p-3 text-primary">
                          <Icon className="h-5 w-5" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold">{item.title}</h3>
                          <p className="mt-2 text-muted-foreground">{item.body}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-3xl border border-border bg-card p-8 shadow-soft">
              <span className="eyebrow">Why it works</span>
              <h3 className="mt-4 text-2xl font-bold md:text-3xl">Built for real purchase behavior, not just a nice demo.</h3>
              <div className="mt-6 space-y-4">
                {trustPoints.map((point) => (
                  <div key={point} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
                    <p className="text-muted-foreground">{point}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 rounded-2xl bg-primary/5 p-6">
                <p className="text-sm font-semibold text-primary">Result</p>
                <p className="mt-2 text-muted-foreground">
                  Customers get a smoother group-buying experience, and merchants avoid placing duplicate or partially funded orders.
                </p>
              </div>

              <div className="mt-6 rounded-2xl border border-border bg-background p-6">
                <p className="text-sm font-semibold text-foreground">Built for premium gifting, group purchases, and social sharing.</p>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                  Splitkart helps brands convert high-consideration purchases by turning one expensive order into a coordinated checkout flow that works across product pages, carts, branded payment links, and the merchant dashboard.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-tight max-w-4xl">
          <div className="mx-auto max-w-2xl text-center">
            <span className="eyebrow">FAQs</span>
            <h2 className="mt-4 text-3xl font-bold md:text-5xl">Common questions about the split flow.</h2>
            <p className="mt-4 text-lg text-muted-foreground">
              A quick walkthrough of what organizers, friends, and merchants can expect.
            </p>
          </div>

          <div className="mt-10 rounded-3xl border border-border bg-card p-2 shadow-soft">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((item, index) => (
                <AccordionItem key={item.q} value={`faq-${index}`} className="border-border px-5">
                  <AccordionTrigger className="text-left text-base font-semibold hover:no-underline">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-muted-foreground">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <CtaBand />
    </SiteLayout>
  );
}

export default HowSplitPaymentsWork;
